<script lang="ts" setup>
import type { StakeAccountWithState } from '~/store/stake-accounts'
import { useWallet } from '@solana/wallet-adapter-vue'
import { JSTAKING_VALIDATOR_VOTE } from '~/config'
import { DEACTIVATE_ACTION, useStakeAccountStore, WITHDRAW_ACTION } from '~/store/stake-accounts'

const selectedStakeAccounts = ref<{ label: string, value: StakeAccountWithState }[]>([])

const stakeAccountStore = useStakeAccountStore()
const { connected } = useWallet()

const loading = computed(() => stakeAccountStore.loading)
const accountsFull = computed(() => {
  return stakeAccountStore.accountsFull.filter(
    acc => acc.stakeAccount.account.data?.parsed?.info?.stake?.delegation?.voter === JSTAKING_VALIDATOR_VOTE,
  )
})

const options = computed(() => {
  const accounts = accountsFull.value
  return accounts.sort((a, b) => {
    const order: { [key: string]: number } = {
      active: 1,
      inactive: 2,
      activating: 3,
      deactivating: 4,
    }

    return order[a.state] - order[b.state]
  })?.map((acc, idx) => {
    return {
      label: `Stake #${idx + 1}`,
      value: acc,
    }
  })
})

const actionType = ref(DEACTIVATE_ACTION)

watch(selectedStakeAccounts, (d) => {
  stakeAccountStore.dataSelected = d.map(d => String(d.value.stakeAccount.pubkey))
  const firstSelectedStake = d[0]
  if (firstSelectedStake) {
    actionType.value = firstSelectedStake.value.state === 'active' ? DEACTIVATE_ACTION : WITHDRAW_ACTION
  }
}, { deep: true })
</script>

<template>
  <div v-if="loading" class="unstake-card__loading">
    <q-spinner
      color="zinc-500"
      size="5em"
      :thickness="2"
    />
  </div>
  <not-connected v-else-if="!connected" />
  <not-stakes v-else-if="accountsFull.length === 0" />
  <div v-else class="unstake-card">
    <select-widget
      v-model="selectedStakeAccounts"
      :options="options" title="My Stake Accounts"
      :subtitle="`${accountsFull.length} available to unstake`"
      size="md"
    >
      <template #option="{ option, index }">
        <unstake-option-item :option="option.value" :index="index" />
      </template>
    </select-widget>
  </div>
</template>

<style lang="scss">
.unstake-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  &__loading {
    width: 100%;
    height: 80%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__action {
    margin-top: auto;

    @media (max-width: $breakpoint-xs) {
      margin-top: 50px;
    }

    .not-selected {
      color: $zinc-500;
      height: 50px;
      cursor: not-allowed;
      line-height: 100%;
      font-weight: 500;
      display: flex;
      align-items: center;
      justify-content: center;

      @media (max-width: $breakpoint-xs) {
        font-size: 14px;
        height: 42px;
        border-radius: $border-radius-sm;
      }
    }

    .q-btn {
      width: 100%;
    }
  }
}

.stake-menu-item {
  display: flex;
  gap: $border-radius-md;
  padding: 4px 12px 4px 0;

  .stake {
    &-indicator {
      width: 20px;
      height: 24px;
      color: $zinc-50;
      display: flex;
      justify-content: space-between;
      justify-items: center;

      span[data-name='label'] {
        overflow: hidden;
        font-variant-numeric: slashed-zero;
        text-overflow: ellipsis;
      }

      svg {
        width: 100%;
        color: $primary;
      }
    }

    &-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;

      .status {
        border-radius: 4px;
        border: 1px solid rgba($solana-green-700, 0.1);
        background: rgba($solana-green-700, 0.1);
        box-shadow:
          0 2px 2px 0 rgba(#09090b, 0.2),
          0 6px 4px -4px rgba(#09090b, 0.4),
          0 1px 1px 0 rgba(#fff, 0.2) inset;
        text-transform: uppercase;
        font-weight: 600;
        padding: $space-xxs $space-sm;
        color: $solana-green-500;
      }
    }
  }
}
</style>
